import React, { useState } from 'react';
import { gql, useMutation } from '@apollo/client';

const UPDATE_CONTEXT = gql`
  mutation UpdateContext($name: String!, $content: String!) {
    updateContext(name: $name, content: $content) {
      context {
        id
        name
        version
      }
      oldVersion
      newVersion
      bumpType
      message
      affectedContexts
    }
  }
`;

interface Props {
  name: string;
  currentVersion: string;
  currentContent: string;
  onClose: () => void;
}

export function ContextUpdate({ name, currentVersion, currentContent, onClose }: Props) {
  const [content, setContent] = useState(currentContent);

  const [updateContext, { loading, data }] = useMutation(UPDATE_CONTEXT, {
    onCompleted: () => {
      // Success handled below
    },
    onError: (error) => {
      alert(`Error: ${error.message}`);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateContext({
      variables: { name, content },
    });
  };

  if (data) {
    const result = data.updateContext;
    return (
      <div className="p-6 bg-green-50 border border-green-200 rounded-lg">
        <h3 className="text-lg font-bold text-green-800 mb-4">✅ Update Successful</h3>
        <div className="space-y-2 text-sm mb-4">
          <p><strong>Version:</strong> {result.oldVersion} → {result.newVersion}</p>
          <p><strong>Change Type:</strong> <span className={`font-bold ${
            result.bumpType === 'MAJOR' ? 'text-red-600' :
            result.bumpType === 'MINOR' ? 'text-blue-600' :
            'text-gray-600'
          }`}>{result.bumpType}</span></p>
          <p>{result.message}</p>
          {result.affectedContexts > 0 && (
            <p className="text-yellow-600">
              ⚠️ Warning: {result.affectedContexts} contexts may be affected
            </p>
          )}
        </div>
        <button
          onClick={onClose}
          className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
        >
          Done
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="p-4">
      <h3 className="text-lg font-bold mb-4">
        Update: {name} <span className="text-sm text-gray-500">v{currentVersion}</span>
      </h3>

      <div className="mb-4">
        <label className="block text-sm font-medium mb-1">New Content</label>
        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          className="w-full border rounded px-3 py-2 h-48 font-mono text-sm"
          required
        />
        <p className="text-xs text-gray-500 mt-1">
          The system will automatically detect if this is a major, minor, or patch change.
        </p>
      </div>

      <div className="flex gap-2">
        <button
          type="submit"
          disabled={loading}
          className="bg-primary text-white px-4 py-2 rounded hover:bg-primary/90 disabled:opacity-50"
        >
          {loading ? 'Analyzing changes...' : 'Update Context'}
        </button>
        <button
          type="button"
          onClick={onClose}
          className="bg-gray-200 text-gray-700 px-4 py-2 rounded hover:bg-gray-300"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
